// Universal CCSD (Clinical Coding & Schedule Development) & BUPA code mappings.
// Generated from authoritative clinician procedure data cross-referenced with LIPS specialities.

export interface CcsdEntry {
  /** Official CCSD / Bupa procedure code (e.g. "W3712"). Stored uppercase. */
  code: string;
  /** Human-readable procedure / treatment name shown in autocomplete & search. */
  treatment: string;
  /** LIPS speciality slugs used to resolve matching specialists. */
  specialities: readonly string[];
}

export const CCSD_CODES: readonly CcsdEntry[] = [
  {
    code: '20320',
    treatment: 'Inpatient care',
    specialities: [
      'private-gp',
      'haematology',
      'general-surgery',
      'geriatrics',
      'plastic-surgery',
      'psychiatry',
      'maxillofacial',
      'dental-services',
    ],
  },
  {
    code: 'X0004',
    treatment:
      'Clinical supervision and planning for the delivery of chemotherapy and/or systemic anti-cancer therapy for 1-28 days',
    specialities: ['haematology'],
  },
  {
    code: 'W3650',
    treatment:
      'Diagnostic aspiration and trephine biopsy of bone marrow, including analysis',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'haematology',
    ],
  },
  {
    code: 'W3032',
    treatment:
      'Removal of fixator/frame/pins/wires and change of plaster (as sole procedure)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'plastic-surgery',
    ],
  },
  {
    code: 'AB330',
    treatment: 'AB330',
    specialities: ['neurosurgery', 'neurology', 'psychiatry'],
  },
  {
    code: '11320',
    treatment: '11320',
    specialities: [
      'private-gp',
      'haematology',
      'rheumatology',
      'cardiology',
      'neurosurgery',
      'geriatrics',
    ],
  },
  {
    code: '20110',
    treatment: '20110',
    specialities: [
      'cardiology',
      'geriatrics',
      'neurosurgery',
      'childrens-health-paediatrics',
    ],
  },
  {
    code: 'C8440',
    treatment:
      'Retinal examination under anaesthetic (including retinopexy if necessary)',
    specialities: ['ophthalmology'],
  },
  {
    code: 'W9030',
    treatment:
      'Injection(s) +/- aspiration, into joint, cyst, bursa with image guidance - unilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'arthrosamid',
      'plastic-surgery',
    ],
  },
  {
    code: 'A2223',
    treatment: 'Cerebral angiogram',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'G6500',
    treatment:
      'Diagnostic oesophago-gastro-duodenoscopy (OGD) includes forceps biopsy, biopsy urease test and dye spray',
    specialities: ['gastric-alimetry', 'general-surgery', 'ent'],
  },
  {
    code: 'W8500',
    treatment:
      'Multiple arthroscopic operations on knee (including meniscectomy, chondroplasty, drilling or microfracture) - unilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'geriatrics',
    ],
  },
  {
    code: 'A6510',
    treatment: 'Carpal tunnel release (open)',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'neurosurgery'],
  },
  {
    code: 'W7420',
    treatment:
      'Autograft anterior cruciate ligament reconstruction +/- meniscectomy',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W3712',
    treatment: 'Primary total hip replacement +/- cement',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'geriatrics',
    ],
  },
  {
    code: '64300',
    treatment: '64300',
    specialities: [
      'cardiology',
      'geriatrics',
      'rheumatology',
      'neurology',
      'haematology',
    ],
  },
  {
    code: 'F0910',
    treatment: 'Surgical removal of impacted/buried tooth/teeth',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'W9035',
    treatment:
      'Injections(s) +/- aspiration into two or more joints, cysts, bursae with image guidance - bilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'arthrosamid',
    ],
  },
  {
    code: 'C7122',
    treatment:
      'Ultrasound phacoemulsification of cataract, with lens implant - unilateral (including topical or local anaesthetic)',
    specialities: ['ophthalmology'],
  },
  {
    code: 'N1920',
    treatment: 'Microsurgical varicocelectomy',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'H2002',
    treatment:
      'Diagnostic colonoscopy, includes forceps biopsy of colon and ileum',
    specialities: ['general-surgery', 'gut-health-test', 'ent'],
  },
  {
    code: 'A5755',
    treatment:
      'Nerve Root Block +/- Image Guidance (including Bilateral) LUMBAR',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'neurosurgery'],
  },
  {
    code: 'E1432',
    treatment:
      'FESS: excision uncinate process and anterior ethmoid bulla, antrostomy/antral puncture, simple polypectomy and attention to turbinates (bilateral)',
    specialities: ['ent', 'dental-services'],
  },
  {
    code: 'N3030',
    treatment: 'Circumcision',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'G8082',
    treatment:
      'Diagnostic oesophago-gastro-duodenoscopy (OGD) and immediate colonoscopy includes forceps biopsies, biopsy test and dye spray (as sole procedure)',
    specialities: ['gastric-alimetry', 'general-surgery', 'ent'],
  },
  {
    code: 'X3570',
    treatment: 'Therapeutic venesection',
    specialities: ['haematology'],
  },
  {
    code: 'J1831',
    treatment: 'Robotic assisted cholecystectomy',
    specialities: ['general-surgery', 'liver-health-liver-scans'],
  },
  {
    code: 'X0001',
    treatment:
      'Clinical supervision and planning for the delivery of chemotherapy and/or systemic anti-cancer therapy for 0-7 days',
    specialities: ['haematology'],
  },
  {
    code: 'B2986',
    treatment:
      'Reconstruction of breast using deep inferior epigastric perforator flap (DIEP) (including delayed reconstruction) - unilateral (single flap)',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'M4510',
    treatment:
      'Diagnostic endoscopic examination of bladder (flexible cystoscopy) including any biopsy',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'W9032',
    treatment:
      'Injection of viscosupplement into joint with image guidance - unilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'arthrosamid',
    ],
  },
  {
    code: 'X0003',
    treatment:
      'Clinical supervision and planning for the delivery of chemotherapy and/or systemic anti-cancer therapy for 1-21 days',
    specialities: ['haematology'],
  },
  {
    code: 'Q1701',
    treatment: 'Laparoscopic excision of endometriosis, +/-ureterolysis',
    specialities: [
      'gynaecology-and-obstetrics',
      'female-fertility',
      'haematology',
    ],
  },
  {
    code: 'A5745',
    treatment:
      'Medial branch block injection(s) +/- image guidance (including bilateral) LUMBAR',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'W3090',
    treatment: 'Core decompression of knee',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'Q1700',
    treatment:
      'Therapeutic hysteroscopic operations on uterus (including endometrial ablation excluding microwave or radiofrequency ablation) +/- Mirena coil insertion',
    specialities: ['gynaecology-and-obstetrics', 'haematology'],
  },
  {
    code: 'A6710',
    treatment: 'Cubital tunnel release (open) (without transposition)',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'E0360',
    treatment: 'Septoplasty of nose (including attention to turbinates)',
    specialities: ['ent', 'dental-services'],
  },
  {
    code: 'T7915',
    treatment: 'Arthroscopic rotator cuff repair greater than 2cm',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'physiotherapy'],
  },
  {
    code: 'Q1800',
    treatment:
      'Hysteroscopy (including biopsy, dilatation, curettage and resection of polyp(s) +/- Mirena coil insertion)',
    specialities: ['gynaecology-and-obstetrics', 'haematology'],
  },
  {
    code: '64302',
    treatment:
      'Transoesophageal echocardiography (including reporting) (as sole procedure)',
    specialities: ['cardiology'],
  },
  {
    code: '20330',
    treatment: 'Inpatient consultation by second specialist or for emergency',
    specialities: [
      'private-gp',
      'haematology',
      'geriatrics',
      'plastic-surgery',
    ],
  },
  {
    code: 'W7718',
    treatment:
      'Primary arthroscopic shoulder stabilisation procedure (including labral/SLAP/tendon repair)',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: 'W4210',
    treatment:
      'Total prosthetic replacement of knee joint +/- cement +/- patella resurfacing - unilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'XR306',
    treatment: 'Endovascular treatment of cerebral aneurysm',
    specialities: ['radiology'],
  },
  {
    code: 'A5753',
    treatment:
      'Nerve Root Block +/- Image Guidance (including Bilateral) CERVICAL',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'neurosurgery'],
  },
  {
    code: 'D1510',
    treatment:
      'Myringotomy and insertion of tube through tympanic membrane (and bilateral)',
    specialities: [
      'ent',
      'audiology-hearing-test-battersea-london',
      'dental-services',
    ],
  },
  {
    code: 'W9033',
    treatment:
      'Injection of viscosupplement into joint with image guidance - bilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'arthrosamid',
    ],
  },
  {
    code: '20144',
    treatment: '20144',
    specialities: ['cardiology', 'geriatrics', 'neurology'],
  },
  {
    code: 'Q0740',
    treatment:
      'Total abdominal hysterectomy, +/- oophorectomy, +/- ureterolysis',
    specialities: ['gynaecology-and-obstetrics'],
  },
  {
    code: 'W8194',
    treatment:
      'Arthroscopic subacromial decompression and excision of distal clavicle (including arthroscopic procedures in glenohumeral joint)',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: 'M6192',
    treatment:
      'Robotic assisted radical prostatectomy, reconstruction of bladder neck including bilateral pelvic lymphadenectomy (including cystoscopy)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'V2540',
    treatment:
      'Posterior excision of disc prolapse (including microdiscectomy +/- decompression) - lumbar region (1 or 2 levels)',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'A5211',
    treatment: 'Epidural injection (caudal)',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'N1340',
    treatment: 'Biopsy of testis',
    specialities: ['urology-and-andrology', 'sexual-health'],
  },
  {
    code: 'W5200',
    treatment: 'Unicompartmental knee replacement - unilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W8200',
    treatment: 'Arthroscopic meniscectomy (including debridement) - unilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W8230',
    treatment: 'Arthroscopic meniscal repair',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'X3750',
    treatment: 'Botulinum toxin injections to muscle',
    specialities: ['radiology', 'maxillofacial'],
  },
  {
    code: 'W7872',
    treatment:
      'Arthroscopic arthrolysis of shoulder contracture +/- manipulation/injection',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'arthrosamid'],
  },
  {
    code: 'W8193',
    treatment: 'Arthroscopic subacromial decompression',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'M6583',
    treatment: 'Transperineal MRI - US fusion targeted prostate biopsy',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'A5756',
    treatment:
      'Nerve Root Block +/- Image Guidance (including Bilateral) CAUDAL',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'neurosurgery'],
  },
  {
    code: 'F3440',
    treatment: 'Tonsillectomy - adult (and bilateral)',
    specialities: ['maxillofacial', 'dental-services', 'ent'],
  },
  {
    code: 'W2100',
    treatment:
      'Primary open reduction of intra-articular fracture of long bone with internal fixation, e.g. proximal humerus or proximal tibia (+/- arthroscopic assistance)',
    specialities: ['trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V2030',
    treatment: 'Arthroplasty of temporomandibular bone joint - unilateral',
    specialities: [
      'neurosurgery',
      'trauma-and-orthopaedics-lower-limb',
      'maxillofacial',
    ],
  },
  {
    code: 'V2162',
    treatment:
      'Therapeutic arthroscopic operation of temporomandibular joint +/- lysis and/or lavage ? bilateral',
    specialities: [
      'neurosurgery',
      'trauma-and-orthopaedics-lower-limb',
      'maxillofacial',
    ],
  },
  {
    code: 'S0633',
    treatment:
      'Excision of lesion of skin or subcutaneous tissue - up to three, Trunk & Limbs (excluding lipoma)',
    specialities: [
      'dermatology',
      'plastic-surgery',
      'lumps-and-bumps',
      'physiotherapy',
    ],
  },
  {
    code: 'W7440',
    treatment:
      'Allograft anterior cruciate ligament reconstruction +/- meniscectomy',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'S5712',
    treatment:
      'Debridement of wound (and surgical toilet) - over 25cm2 in area',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'W1800',
    treatment:
      'Drainage/debridement of bone(s) (including sequestrectomy for osteomyelitis)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'plastic-surgery',
    ],
  },
  {
    code: 'X0002',
    treatment:
      'Clinical supervision and planning for the delivery of chemotherapy and/or systemic anti-cancer therapy for 1-14 days',
    specialities: ['haematology'],
  },
  {
    code: 'J1830',
    treatment: 'Laparoscopic cholecystectomy',
    specialities: ['general-surgery', 'liver-health-liver-scans'],
  },
  {
    code: 'T7982',
    treatment:
      'Arthroscopic subacromial decompression and rotator cuff repair (including arthroscopic procedures in glenohumeral joint)',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'physiotherapy'],
  },
  {
    code: '64301',
    treatment: '64301',
    specialities: ['cardiology', 'geriatrics'],
  },
  {
    code: 'V2282',
    treatment:
      'Prosthetic intervertebral disc replacement ? cervical region (1 or 2 levels) +/- spinal cord monitoring',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V2542',
    treatment:
      'Posterior excision of disc prolapse with undercutting facetectomy +/- decompression - lumbar region (1 or 2 levels)',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V2560',
    treatment: 'Decompression for central spinal stenosis (1 or 2 levels)',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'N1710',
    treatment: 'Vasectomy (bilateral ligation of Vas Deferens)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'F0200',
    treatment: 'Excision of lesion of lip',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'W4218',
    treatment: 'Customised total prosthetic replacement of knee joint',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'Q0790',
    treatment:
      'Laparoscopic total hysterectomy, +/- oophorectomy, +/- ureterolysis',
    specialities: ['gynaecology-and-obstetrics', 'haematology'],
  },
  {
    code: 'S0656',
    treatment: 'Removal of lipoma',
    specialities: [
      'dermatology',
      'plastic-surgery',
      'lumps-and-bumps',
      'physiotherapy',
    ],
  },
  {
    code: 'W4200',
    treatment:
      'Complex primary total knee replacement (i.e. including bone graft, augmentation or osteotomy)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'geriatrics',
    ],
  },
  {
    code: 'G2120',
    treatment: 'High resolution oesophageal manometry',
    specialities: ['gastric-alimetry', 'general-surgery', 'ent'],
  },
  {
    code: '20140',
    treatment: '20140',
    specialities: ['cardiology', 'neurosurgery', 'neurology', 'geriatrics'],
  },
  {
    code: 'XR390',
    treatment:
      'Embolisation of arteriovenous malformation of brain or major organ',
    specialities: ['radiology'],
  },
  {
    code: 'C1210',
    treatment: 'Excision of lesion of eyelid',
    specialities: ['ophthalmology'],
  },
  {
    code: 'CD023',
    treatment: 'CD023',
    specialities: ['ophthalmology', 'cardiology', 'neurology', 'geriatrics'],
  },
  {
    code: 'T7231',
    treatment:
      'Open release of constriction of sheath of tendon (e.g. trigger finger)',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'physiotherapy'],
  },
  {
    code: 'T8050',
    treatment:
      'Surgical release of humeral epicondylitis (lateral or medial) (e.g. ?Tennis Elbow?)',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'physiotherapy'],
  },
  {
    code: 'W1940',
    treatment:
      'Primary open reduction of short bone with fixation (including intra-articular)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'M6582',
    treatment:
      'Transperineal template-guided biopsies of the prostate under image guidance',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'A5765',
    treatment:
      'Neurolytic Root Block (Radiofrequency denervation, Thermocoagulation, Cryotherapy or Phenol, including Rhizolysis) +/- Image Guidance (including Bilateral) LUMBAR',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'M4820',
    treatment:
      'Insertion of Urethral Catheterisation Device (UCD) using guidewire into bladder for difficult or failed catheterisation',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'Q1280',
    treatment: 'Introduction of a Mirena coil',
    specialities: ['gynaecology-and-obstetrics'],
  },
  {
    code: 'S0651',
    treatment:
      'Removal of benign lesion on trunk or limbs less than 10 cm in diameter or on scalp less than 5cm in diameter (excluding lipoma)',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'W1920',
    treatment: 'Primary open reduction of long bone with fixation',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'plastic-surgery',
    ],
  },
  {
    code: 'G4010',
    treatment: 'Pyloromyotomy',
    specialities: ['gastric-alimetry', 'general-surgery', 'ent'],
  },
  {
    code: '20132',
    treatment: '20132',
    specialities: ['general-surgery', 'cardiology'],
  },
  {
    code: 'CD030',
    treatment: 'CD030',
    specialities: ['ophthalmology', 'cardiology', 'geriatrics'],
  },
  {
    code: 'T9020',
    treatment:
      'Sentinel node mapping and sampling with blue dye and radioactive probe for breast cancer',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'A6570',
    treatment: 'Carpal tunnel release (endoscopic) - bilateral',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'neurosurgery'],
  },
  {
    code: 'T5410',
    treatment: "Dupuytren's subcutaneous fasciotomy",
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'Q0920',
    treatment: 'Myomectomy (including laparoscopically) +/- ureterolysis',
    specialities: ['gynaecology-and-obstetrics', 'haematology'],
  },
  {
    code: 'W2830',
    treatment:
      'Removal of internal fixation from bone / joint, excluding K-wires +/- image guidance',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W5050',
    treatment: 'Reverse polarity arthroplasty of shoulder',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: 'W8110',
    treatment: 'Arthroscopic excision of calcific deposits from shoulder',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: 'W3040',
    treatment:
      'Application of IIizarov frame for secondary non-union / mal-union (including osteotomy)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'V3362',
    treatment:
      'Primary posterior fusion with instrumentation +/- decompression +/- discectomy (including Graf stabilisation and all fusion approaches) (lumbar region) including spinal cord monitoring',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'T6910',
    treatment: 'Tenolysis, of extensor (not otherwise specified)',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'A1300',
    treatment: 'Maintenance of cerebroventricular shunt (including revision)',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'D2240',
    treatment: 'Balloon Dilation of the Eustachian Tube',
    specialities: [
      'ent',
      'audiology-hearing-test-battersea-london',
      'dental-services',
    ],
  },
  {
    code: 'E0420',
    treatment: 'Reduction turbinates of nose (trim, radical excision)',
    specialities: ['ent', 'dental-services'],
  },
  {
    code: 'F3480',
    treatment: 'Adenotonsillectomy (and bilateral)',
    specialities: ['maxillofacial', 'dental-services', 'ent'],
  },
  {
    code: 'W5000',
    treatment: 'Primary total shoulder replacement',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: 'T6914',
    treatment: 'Tenolysis of extensor tendon of hand',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'physiotherapy',
      'plastic-surgery',
    ],
  },
  {
    code: 'T6980',
    treatment: 'Tenolysis, of flexor tendon (not otherwise specified)',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
      'plastic-surgery',
    ],
  },
  {
    code: 'W0285',
    treatment: 'Trapezio-metacarpal joint surface replacement',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'S5730',
    treatment:
      'Surgical toilet and debridement of deep wound (including traumatic or post-operative aetiology)',
    specialities: [
      'dermatology',
      'plastic-surgery',
      'lumps-and-bumps',
      'geriatrics',
    ],
  },
  {
    code: 'W3740',
    treatment:
      'Second, third or further revision total hip replacement (excluding acetabular liner and head changes)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'geriatrics',
    ],
  },
  {
    code: 'Q1281',
    treatment:
      'Removal and/or replacement of an embedded / migrated Mirena coil (as sole procedure)',
    specialities: ['gynaecology-and-obstetrics'],
  },
  {
    code: 'W9050',
    treatment: 'Shoulder hydrodistension +/- image guidance',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: 'T6800',
    treatment:
      'Delayed or secondary repair of tendon (including graft, transfer and/or prosthesis) (not otherwise specified)',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'W2380',
    treatment: 'Locked intramedullary nailing of fractured long bone',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W1911',
    treatment: 'Core decompression of hip',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W7180',
    treatment:
      'Harvesting and injection of bone marrow aspirate concentrate (BMAC)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'arthrosamid',
    ],
  },
  {
    code: 'W8620',
    treatment: 'Therapeutic arthroscopy examination of hip joint, +/- biopsy',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W9040',
    treatment:
      'Injection(s) +/- aspiration, into joint, cyst, bursa - unilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'arthrosamid',
    ],
  },
  {
    code: 'W9046',
    treatment: 'Platelet rich plasma injections for knee osteoarthritis',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'arthrosamid',
    ],
  },
  {
    code: 'IM347',
    treatment: 'IM347',
    specialities: ['general-surgery'],
  },
  {
    code: 'Q3800',
    treatment:
      'Laparoscopy and therapeutic procedures (including laser, diathermy and destruction e.g. endometriosis, adhesiolysis, tubal and ovarian surgery, +/-ureterolysis)',
    specialities: [
      'gynaecology-and-obstetrics',
      'female-fertility',
      'haematology',
    ],
  },
  {
    code: '20141',
    treatment: '20141',
    specialities: ['cardiology', 'geriatrics'],
  },
  {
    code: 'A6810',
    treatment:
      'Neurolysis and transposition of peripheral nerve (excludes carpal tunnel release)',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'neurosurgery'],
  },
  {
    code: 'H2003',
    treatment:
      'Therapeutic colonoscopy with snare loop biopsy or excision of lesion',
    specialities: ['general-surgery', 'gut-health-test', 'ent'],
  },
  {
    code: 'W9240',
    treatment:
      'Examination/ manipulation of joint under general anaesthetic +/- injection +/- arthrogram (as sole procedure)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'arthrosamid',
      'geriatrics',
    ],
  },
  {
    code: 'W3020',
    treatment: 'Adjustments to IIizarov frame/rings',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'A4500',
    treatment: 'Open operations on spinal cord',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'A5743',
    treatment:
      'Medial branch block injection(s) +/- image guidance (including bilateral) CERVICAL',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'V2200',
    treatment:
      'Posterior decompression +/- foraminotomy - cervical region (1 or 2 levels)',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'E1480',
    treatment:
      'Endoscopic exploration frontal sinus beyond frontoethmoid recess and bilateral',
    specialities: ['ent', 'dental-services'],
  },
  {
    code: 'W2620',
    treatment:
      'Manipulation under anaesthesia of fractured nose (as sole procedure)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'dental-services',
    ],
  },
  {
    code: 'T2000',
    treatment: 'Primary repair of inguinal hernia',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
      'general-surgery',
    ],
  },
  {
    code: 'V3350',
    treatment:
      'Combined anterior approach discectomy, decompression and fusion and posterior fusion (lumbar region) including spinal cord monitoring',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: '20170',
    treatment:
      'Blind challenge test for food allergy under medical supervision',
    specialities: ['general-surgery', 'neurosurgery', 'cardiology'],
  },
  {
    code: 'C3780',
    treatment:
      'Injection of botulinum toxin into extraocular or periocular muscles',
    specialities: ['ophthalmology', 'neurosurgery'],
  },
  {
    code: 'C1810',
    treatment:
      'Correction of ptosis of eyelid - simple (including tarsomullerectomy)',
    specialities: ['ophthalmology'],
  },
  {
    code: 'C3530',
    treatment: 'Surgical correction of squint with adjustable sutures',
    specialities: ['ophthalmology'],
  },
  {
    code: 'N0680',
    treatment:
      'Orchidectomy and excision of spermatic cord (+/- insertion of prosthesis)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'N0700',
    treatment: 'Excision of lesion of testis',
    specialities: ['urology-and-andrology', 'sexual-health'],
  },
  {
    code: 'N0920',
    treatment: 'Orchiopexy',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'N1100',
    treatment: 'Correction of hydrocele(s) ? unilateral',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'N2710',
    treatment: 'Excision of lesion of penis',
    specialities: ['urology-and-andrology', 'sexual-health'],
  },
  {
    code: 'N2781',
    treatment: "Lue's procedure for Peyronie's disease",
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'W1080',
    treatment: 'Osteotomy of long bone, +/- fixation (including graft)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W5830',
    treatment: 'Robotic assisted knee replacement - unilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W6019',
    treatment: 'Ankle syndesmosis reconstruction',
    specialities: ['trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'T5900',
    treatment: 'Excision of ganglion',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
      'plastic-surgery',
    ],
  },
  {
    code: 'W7400',
    treatment: 'Reconstruction of one or two ligaments not elsewhere specified',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W7485',
    treatment:
      'Small joint (e.g. interphalangeal/ metacarpo-phalangeal joint) ligament reconstruction',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'F0950',
    treatment: 'Surgical removal of complicated buried roots',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'F2310',
    treatment: 'Excision/destruction of lesion of tongue',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'F4230',
    treatment: 'Removal of excess mucosa from mouth',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'S0654',
    treatment:
      'Removal of benign lesion on head and neck (excluding scalp) which is closed by primary closure or advancement flap (excluding lipoma)',
    specialities: [
      'dermatology',
      'plastic-surgery',
      'lumps-and-bumps',
      'maxillofacial',
      'dental-services',
    ],
  },
  {
    code: 'V2161',
    treatment:
      'Therapeutic arthroscopic operation of temporomandibular joint +/- lysis and/or lavage ? unilateral',
    specialities: [
      'neurosurgery',
      'trauma-and-orthopaedics-lower-limb',
      'maxillofacial',
    ],
  },
  {
    code: 'S0820',
    treatment:
      'Curettage/cryotherapy of lesions of skin (including cauterisation) - four or more',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S1110',
    treatment:
      'Curettage/cryotherapy of lesion of skin (including cauterisation) - up to three',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S1500',
    treatment: 'Biopsy of skin or subcutaneous tissue',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'W1646',
    treatment: 'Open reduction/internal fixation of sacro-iliac joint',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W3733',
    treatment:
      'Revision of total hip replacement (including insertion of reconstruction rings, plates, screws, etc., and/or impaction bone grafting to acetabulum and/or femur) - unilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'geriatrics',
    ],
  },
  {
    code: 'W5810',
    treatment: 'Patella resurfacing (as sole procedure)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W8520',
    treatment:
      'Arthroscopy of knee (including examination under anaesthetic, washout and biopsy) (as sole procedure)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'C7982',
    treatment:
      'Pars plana vitrectomy with internal tamponade, scleral buckling and retinopexy with dissection or excision of epiretinal membrane/macular surgery',
    specialities: ['ophthalmology'],
  },
  {
    code: 'A6600',
    treatment: 'Release of entrapment of deeply placed peripheral nerve',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'neurosurgery',
      'geriatrics',
    ],
  },
  {
    code: 'S1740',
    treatment:
      'Large myocutaneous (muscular/cutaneous) flap (9cm2 or more) (including closure of secondary defect)',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S3530',
    treatment:
      'Split autograft of skin, trunk and limbs - over 25cm2 and up to 5% of body surface area',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'T8003',
    treatment:
      'Major release of muscle for pain or contracture (e.g. Quadriceps) (involving large joint)',
    specialities: ['trauma-and-orthopaedics-lower-limb', 'physiotherapy'],
  },
  {
    code: 'W0300',
    treatment:
      'Multiple procedures on forefoot, distal to and including the tarsometatarsal joint, which involves at least two distinct procedures not intrinsic to each other - unilateral',
    specialities: ['trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'W1320',
    treatment: 'Osteotomy of proximal Femur',
    specialities: ['trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'M4210',
    treatment:
      'Endoscopic resection of lesion of bladder (including cystoscopy)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'G4410',
    treatment:
      'Therapeutic oesophago-gastro-duodenoscopy (OGD) with insertion of prosthesis',
    specialities: ['gastric-alimetry', 'general-surgery', 'ent'],
  },
  {
    code: 'G4610',
    treatment: 'Transoral incisionless fundoplication (TIF)',
    specialities: ['gastric-alimetry', 'general-surgery', 'ent'],
  },
  {
    code: 'S0632',
    treatment:
      'Excision of lesion of skin or subcutaneous tissue - up to three, Head & Neck (excluding lipoma)',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'T6710',
    treatment: 'Primary repair of extensor of hand',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'physiotherapy',
      'plastic-surgery',
    ],
  },
  {
    code: 'K6010',
    treatment:
      'Cardiac pacemaker system introduced through vein (dual chamber)',
    specialities: ['cardiology'],
  },
  {
    code: 'A5110',
    treatment: 'Excision of intradural lesion',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'XR290',
    treatment: 'Cerebral angioplasty +/- insertion of metallic stent',
    specialities: ['radiology'],
  },
  {
    code: 'C7923',
    treatment:
      'Intravitreal injection of pharmaceutical for neovascular age related macular degeneration',
    specialities: ['ophthalmology'],
  },
  {
    code: 'C8200',
    treatment: 'Laser photocoagulation/cryotherapy of lesion of retina',
    specialities: ['ophthalmology'],
  },
  {
    code: 'W1380',
    treatment:
      'Arthroscopic femoro-acetabular surgery for hip impingement syndrome, including labral repair and osteochondroplasty',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W7583',
    treatment: 'Repair of patellar/quadricep tendon',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'C1818',
    treatment: 'Reverse ptosis repair of eyelid',
    specialities: ['ophthalmology'],
  },
  {
    code: 'B2780',
    treatment:
      'Simple mastectomy (including axillary node biopsy) - unilateral',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'B2820',
    treatment:
      'Wide local excision of breast +/- local mobilisation of glandular breast tissue to fill surgical cavity',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'B2880',
    treatment: 'Excision biopsy of breast lesion after localisation',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'W8602',
    treatment: 'Therapeutic arthroscopy of wrist joint (as sole procedure)',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: 'Q4400',
    treatment:
      'Ovarian cystectomy +/- omental biopsy (as sole procedure and including bilateral)',
    specialities: ['gynaecology-and-obstetrics', 'haematology'],
  },
  {
    code: 'S0410',
    treatment: 'Wide excision of sweat glands - including bilateral axillae',
    specialities: [
      'dermatology',
      'plastic-surgery',
      'lumps-and-bumps',
      'physiotherapy',
    ],
  },
  {
    code: 'W7881',
    treatment:
      'Release of contracture of interphalangeal joint of finger (excluding trigger finger or Dupuytren?s disease)',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'physiotherapy'],
  },
  {
    code: 'T6782',
    treatment: 'Repair of distal biceps tendon',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'physiotherapy'],
  },
  {
    code: 'W5600',
    treatment:
      'Primary repair of rupture of acromioclavicular or sternoclavicular joint +/- internal fixation',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'G8083',
    treatment:
      'Therapeutic oesophago-gastro-duodenoscopy (OGD) and immediate colonoscopy includes forceps biopsies, biopsy test and dye spray (as sole procedure)',
    specialities: ['gastric-alimetry', 'general-surgery', 'ent'],
  },
  {
    code: 'H2502',
    treatment:
      'Diagnostic flexible sigmoidoscopy (including forceps biopsy and proctoscopy)',
    specialities: ['general-surgery', 'gut-health-test', 'ent'],
  },
  {
    code: 'L8514',
    treatment:
      'Endovenous laser treatment (EVLT) of more than one venous trunk +/- phlebectomies - unilateral',
    specialities: ['general-surgery'],
  },
  {
    code: 'W2310',
    treatment:
      'Secondary open reduction of fractured long bone-and intramedullary fixation or internal fixation for non-union/mal union - including intra-articular (including bone graft)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'Q3980',
    treatment: 'Hysterosalpingo contrast echo sonography',
    specialities: ['gynaecology-and-obstetrics'],
  },
  {
    code: 'M6180',
    treatment:
      'Radical prostatectomy, reconstruction of bladder neck (including bilateral pelvic lymphadenectomy) (including cystoscopy)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'A5763',
    treatment:
      'Neurolytic Root Block (Radiofrequency denervation, Thermocoagulation, Cryotherapy or Phenol, including Rhizolysis) +/- Image Guidance (including Bilateral) CERVICAL',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'V2201',
    treatment:
      'Posterior decompression +/- foraminotomy - cervical region (3 or more levels)',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V4140',
    treatment: 'Removal of posterior spinal implant',
    specialities: [
      'neurosurgery',
      'trauma-and-orthopaedics-lower-limb',
      'plastic-surgery',
    ],
  },
  {
    code: 'W3714',
    treatment:
      'Total hip replacement, +/- cement, after excision arthroplasty or arthrodesis (including conversion of hemiarthroplasty or revision of other previous hip surgery which involved internal fixation)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'D1440',
    treatment:
      'Combined approach tympanoplasty - intact canal wall tympanoplasty',
    specialities: [
      'ent',
      'audiology-hearing-test-battersea-london',
      'dental-services',
    ],
  },
  {
    code: 'E0412',
    treatment:
      'Reduction turbinates of nose (laser, diathermy, out fracture etc)',
    specialities: ['ent', 'dental-services'],
  },
  {
    code: 'E2010',
    treatment: 'Adenoidectomy',
    specialities: ['ent', 'dental-services'],
  },
  {
    code: 'F3240',
    treatment: 'Operations on uvula',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'F3400',
    treatment: 'Tonsillectomy - child (and bilateral)',
    specialities: ['maxillofacial', 'dental-services', 'ent'],
  },
  {
    code: 'W7860',
    treatment: 'Arthroscopic arthrolysis of elbow (as sole procedure)',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: '11300',
    treatment: '11300',
    specialities: ['private-gp', 'general-surgery'],
  },
  {
    code: 'J0310',
    treatment: 'Resection of liver tumour(s)',
    specialities: ['general-surgery', 'liver-health-liver-scans'],
  },
  {
    code: 'J5500',
    treatment: 'Total pancreatectomy and excision of surrounding tissue',
    specialities: ['general-surgery', 'liver-health-liver-scans'],
  },
  {
    code: 'J6981',
    treatment: 'Robotic assisted splenectomy',
    specialities: ['general-surgery', 'liver-health-liver-scans'],
  },
  {
    code: 'T2409',
    treatment:
      'Robotic assisted repair of umbilical/paraumbilical hernia requiring mesh',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
      'general-surgery',
    ],
  },
  {
    code: 'T2780',
    treatment:
      'Open Component Separation Technique (CST) repair for complex abdominal hernia with mesh',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
      'general-surgery',
    ],
  },
  {
    code: 'T8100',
    treatment: 'Open biopsy of muscle or soft tissue lesion',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
      'general-surgery',
    ],
  },
  {
    code: 'T8540',
    treatment: 'Open block dissection of para-aortic lymph nodeses',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
      'general-surgery',
    ],
  },
  {
    code: 'W0463',
    treatment:
      'Complex procedure to mid foot and hindfoot without autogenous bone graft (osteotomy/fusion +/- tendon transfers/fixation)',
    specialities: ['trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'A0400',
    treatment:
      'Biopsy of lesion of tissue of brain (including via a burr hole or stealth guided)',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'A5210',
    treatment: 'Epidural injection (lumbar)',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'A5790',
    treatment:
      'Sacroiliac joint injection under image guidance (and bilateral)',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'V2500',
    treatment:
      'Primary posterior fusion +/- decompression +/- discectomy - lumbar region (1 or 2 levels) including spinal cord monitoring',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V2950',
    treatment:
      'Anterior discectomy, decompression and fusion (including bone grafting) - cervical region (1 or 2 levels)',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'C1700',
    treatment: 'Total reconstruction of eyelid - unilateral',
    specialities: ['ophthalmology'],
  },
  {
    code: 'C1812',
    treatment: 'Correction of ptosis of eyelid - complex',
    specialities: ['ophthalmology'],
  },
  {
    code: 'C2650',
    treatment: 'Probing of nasolacrimal system +/- syringing and/or irrigation',
    specialities: ['ophthalmology'],
  },
  {
    code: 'C3113',
    treatment: 'Surgical correction of squint - bilateral',
    specialities: ['ophthalmology'],
  },
  {
    code: 'M7313',
    treatment: 'Repair of penile shaft hypospadias',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'S2220',
    treatment: 'Neurovascular island flap',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S3102',
    treatment: 'Thinning of skin graft flap',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'D1020',
    treatment: 'Modified radical mastoidectomy (including meatoplasty)',
    specialities: ['ent', 'audiology-hearing-test-battersea-london'],
  },
  {
    code: 'E0330',
    treatment: 'Biopsy of septum of nose',
    specialities: ['ent'],
  },
  {
    code: 'M8120',
    treatment: 'Meatoplasty',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'N1750',
    treatment: 'N1750',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'N2780',
    treatment: "Operation for Peyronie's disease (e.g. Nesbitt's)",
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'N2910',
    treatment: 'Insertion of inflatable penile implant (as a sole procedure)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'S5710',
    treatment:
      'Debridement of wound (and surgical toilet) - up to 25cm2 in area',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'T8780',
    treatment: 'Sentinel node biopsy (except where otherwise listed)',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'W0950',
    treatment:
      'Radical clearance of sarcoma of trunk or limbs, +/- amputation or insertion of prosthesis',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'G2331',
    treatment:
      'Laparoscopic repair of hiatus hernia with anti-reflux procedure (e.g. fundoplication)',
    specialities: ['gastric-alimetry', 'general-surgery'],
  },
  {
    code: 'S4740',
    treatment: 'Drainage of large subcutaneous abscess/haematoma',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'T2400',
    treatment: 'Repair of umbilical/paraumbilical hernia (irrespective of age)',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
      'general-surgery',
    ],
  },
  {
    code: 'S0603',
    treatment: 'Primary excision of malignant lesion - Trunk & Limbs',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S1110B',
    treatment: 'S1110B',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S0653',
    treatment:
      'Removal of benign lesion in muscle or deeper tissue (excluding lipoma)',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'W3030',
    treatment: 'Removal of external fixation from bone',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W4543',
    treatment:
      'Open reduction, internal fixation and complete revision for periprosthetic fracture',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W5201',
    treatment: 'Unicompartmental Knee Replacement - bilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W7410',
    treatment: 'Multiple ligament reconstruction of knee',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W7452',
    treatment:
      '2 stage revision anterior cruciate ligament reconstruction - second stage',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W7580',
    treatment:
      'Open surgical stabilisation of patella, including soft tissue/tendon transfer or release, +/- application of cast (adult)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W7630',
    treatment: 'Reconstruction of medial collateral ligament complex',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W8580',
    treatment:
      'Multiple arthroscopic operations on knee (including meniscectomy, chondroplasty, drilling or microfracture) - bilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'geriatrics',
    ],
  },
  {
    code: 'A6580',
    treatment: 'Carpal tunnel release (open) - bilateral',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'neurosurgery'],
  },
  {
    code: 'T6580',
    treatment:
      'Tendon graft, or tendon transfer (as sole procedure, not otherwise specified)',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'T6752',
    treatment: 'Primary repair of flexor of hand in Zone II',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'physiotherapy'],
  },
  {
    code: 'W3202',
    treatment:
      'Open reduction and internal fixation wedge reconstruction bone graft scaphoid non-union',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: 'W3713',
    treatment:
      'Complex primary total hip replacement (including bone grafting or femoral osteotomy)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'geriatrics',
    ],
  },
  {
    code: 'W3732',
    treatment:
      'Revision of uncemented or cemented total hip replacement without adjunctive procedures - unilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'geriatrics',
    ],
  },
  {
    code: 'W6600',
    treatment: 'Closed reduction of dislocated hip prosthesis',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'geriatrics',
    ],
  },
  {
    code: 'W8600',
    treatment:
      'Therapeutic arthroscopy operation on cavity of joint (not otherwise specified) (as sole procedure)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'geriatrics',
      'plastic-surgery',
    ],
  },
  {
    code: '22026',
    treatment:
      'Intraoperative Neurophysiological Monitoring (including Spinal monitoring) - up to 4 hours',
    specialities: ['cardiology', 'neurosurgery'],
  },
  {
    code: 'A5775',
    treatment:
      'Facet joint injection(s) +/- Image Guidance (including Bilateral) LUMBAR',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'F0911',
    treatment: 'Coronectomy',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'F1210',
    treatment: 'Apicectomy',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'F2220',
    treatment: 'Partial glossectomy for malignancy',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'F2810',
    treatment: 'Excision/destruction of lesion of palate',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'F4210',
    treatment: 'Biopsy of lesion of mouth',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'F5120',
    treatment: 'Open extraction of calculus from submandibular duct',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'S2500',
    treatment: 'Local flap - less than 9cm2',
    specialities: [
      'dermatology',
      'plastic-surgery',
      'lumps-and-bumps',
      'maxillofacial',
    ],
  },
  {
    code: 'S6040',
    treatment: 'Scar revision up to 5cm ? head & neck',
    specialities: [
      'dermatology',
      'plastic-surgery',
      'lumps-and-bumps',
      'maxillofacial',
    ],
  },
  {
    code: 'V0820',
    treatment: 'Open reduction and fixation of fractured jaw',
    specialities: [
      'neurosurgery',
      'trauma-and-orthopaedics-lower-limb',
      'maxillofacial',
    ],
  },
  {
    code: 'V2031',
    treatment: 'Arthroplasty of temporomandibular joint - bilateral',
    specialities: [
      'neurosurgery',
      'trauma-and-orthopaedics-lower-limb',
      'maxillofacial',
    ],
  },
  {
    code: 'W3100',
    treatment: 'Bone graft (as sole procedure)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'maxillofacial',
      'plastic-surgery',
    ],
  },
  {
    code: '25000',
    treatment: 'Incision and drainage (not elsewhere covered)',
    specialities: ['private-gp'],
  },
  {
    code: 'T6722',
    treatment: 'Primary open lengthening of Achilles tendon',
    specialities: ['trauma-and-orthopaedics-lower-limb', 'physiotherapy'],
  },
  {
    code: 'W1643',
    treatment:
      'Open reduction/internal fixation of either posterior wall/column of acetabulum or anterior column of acetabulum',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W1644',
    treatment: 'Open reduction/internal fixation of both columns of acetabulum',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W4230',
    treatment: 'Revision of total replacement of knee joint',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W4242',
    treatment:
      '2 stage revision of total knee replacement for infection - second stage',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'P0310',
    treatment: 'Excision of Bartholin gland',
    specialities: ['gynaecology-and-obstetrics'],
  },
  {
    code: 'B2752',
    treatment: 'Subcutaneous mastectomy',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'B2760',
    treatment:
      'Skin/nipple sparing mastectomy (including axillary node biopsy) - unilateral',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'B3017',
    treatment:
      'Reconstruction of breast using fat transfer (including delayed reconstruction)',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'B3033',
    treatment: 'Simple removal of prosthesis from breast',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'B3037',
    treatment:
      'Removal and replacement of prosthesis into the breast (including capsulectomy) - bilateral',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'B3580',
    treatment:
      'Nipple areola complex reconstruction +/- liposuction and fat transfer',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'C7920',
    treatment:
      'Pars plana vitrectomy with internal tamponade, scleral buckling and retinopexy without dissection or excision of epiretinal membrane/macular surgery',
    specialities: ['ophthalmology'],
  },
  {
    code: 'S4950',
    treatment:
      'Fat transfer, including extraction and volume adjustment, of scar defect following trauma (excluding breast)',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'A6400',
    treatment: 'Repair of peripheral nerve',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'neurosurgery',
      'plastic-surgery',
    ],
  },
  {
    code: 'B2994',
    treatment:
      'Reconstruction of breast using stacked flap (including delayed reconstruction) not elsewhere classified - unilateral (2 flaps)',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'B2996',
    treatment:
      'Reconstruction of breast using deep inferior epigastric perforator flap (DIEP) (including delayed reconstruction) - bilateral (single flap per breast)',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'B2999',
    treatment:
      'Reconstruction of breast using stacked deep inferior epigastric perforator flap (DIEP) (including delayed reconstruction) - unilateral (2 flaps)',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'S0605',
    treatment: 'Secondary excision of malignant lesion - Trunk & Limbs',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S1750',
    treatment:
      'Large muscle flap (9cm2 or more) (including skin graft and closure of secondary defect)',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S3100',
    treatment: 'Re-exploration of free flap',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S3622',
    treatment: 'Full thickness graft, trunk and limbs - up to 9cm2 in area',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S4183',
    treatment:
      'Debridement and primary suture of wound with involvement of deeper tissue - Trunk & Limbs',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S4480',
    treatment: 'Removal of foreign body in deeper tissue',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S5562',
    treatment: 'Release of burn scar contracture, trunk & limbs',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'X0950',
    treatment: 'Amputation of leg below knee',
    specialities: ['radiology', 'plastic-surgery'],
  },
  {
    code: 'X4810',
    treatment: 'Change of cast without general anaesthetic (as sole procedure)',
    specialities: ['radiology', 'plastic-surgery'],
  },
  {
    code: 'T6763',
    treatment: 'Repair of tendon of foot - extensor',
    specialities: ['trauma-and-orthopaedics-lower-limb', 'physiotherapy'],
  },
  {
    code: 'W1910',
    treatment:
      'Primary reduction of fracture of neck of femur and internal fixation',
    specialities: ['trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'W1912',
    treatment:
      'Pinning of head of femur - open or percutaneous (e.g. slipped femoral epiphysis, undisplaced neck fracture)',
    specialities: ['trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'E5533',
    treatment:
      'Percutaneous radiofrequency ablation of malignant neoplasm of lung',
    specialities: ['ent'],
  },
  {
    code: 'W7714',
    treatment: 'W7714',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'A6110',
    treatment: 'Excision of lesion of peripheral nerve (e.g. neurilemma)',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'neurosurgery'],
  },
  {
    code: 'B2785',
    treatment: 'Mastectomy for gynaecomastia - bilateral',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'B3110',
    treatment: 'Reduction mammoplasty - bilateral',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'S3500',
    treatment: 'Split autograft of skin, trunk and limbs - up to 25cm2',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S6043',
    treatment: 'Scar revision over 5cm ? trunk & limbs',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'XR940',
    treatment: 'Retrieval of foreign body under X-ray guidance',
    specialities: ['radiology'],
  },
  {
    code: 'T6450',
    treatment: 'Tenodesis of biceps tendon (as sole procedure)',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'physiotherapy'],
  },
  {
    code: 'W0890',
    treatment: 'Excision distal clavicle (as sole procedure)',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: 'W9112',
    treatment:
      'Manipulation of joint (including intra-articular injection) for ?Frozen Shoulder? (as sole procedure)',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'arthrosamid'],
  },
  {
    code: 'Q2330',
    treatment: 'Salpingectomy (including bilateral) (as sole procedure)',
    specialities: ['gynaecology-and-obstetrics'],
  },
  {
    code: 'W5800',
    treatment:
      'Conversion of a unicompartmental knee replacement to a total replacement of knee joint',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W7451',
    treatment:
      '2 stage revision anterior cruciate ligament reconstruction - first stage',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W7880',
    treatment:
      'Open or arthroscopic release of ankle joint contracture (excluding Achilles tendon lengthening)',
    specialities: ['trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'W9045',
    treatment:
      'Injection(s) +/- aspiration, into two or more joints, cysts, bursae - bilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'arthrosamid',
    ],
  },
  {
    code: 'H4685',
    treatment:
      'Insertion of biodegradable perirectal spacer under image guidance',
    specialities: ['general-surgery', 'gut-health-test'],
  },
  {
    code: 'M2920',
    treatment:
      'Endoscopic insertion/removal of prosthesis into ureter (including bilateral and cystoscopy, +/- pyelography)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'M4320',
    treatment:
      'Endoscopic hydrostatic distension of bladder (including cystoscopy)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'M4514',
    treatment:
      'Endoscopic examination of bladder (rigid cystoscopy) including any biopsy',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'M6530',
    treatment: 'Endoscopic resection of prostate (TUR) (including cystoscopy)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'M6620',
    treatment:
      'Endoscopic incision of outlet of male bladder (including cystoscopy)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'M7071',
    treatment:
      'Transurethral water jet ablation for lower urinary tract symptoms caused by benign prostatic hyperplasia',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'M7920',
    treatment: 'Dilatation of urethra (including cystoscopy)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'N1580',
    treatment: 'Excision of epididymal cyst',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'F1450',
    treatment: 'Exposure of buried tooth',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'B0860',
    treatment: 'Partial thyroidectomy (not elsewhere classified)',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'D0210',
    treatment: 'Excision of lesion of pinna',
    specialities: [
      'ent',
      'audiology-hearing-test-battersea-london',
      'plastic-surgery',
    ],
  },
  {
    code: 'E2810',
    treatment: 'Peroral endoscopic myotomy (POEM) for achalasia',
    specialities: ['ent'],
  },
  {
    code: 'G4430',
    treatment:
      'Therapeutic oesophago-gastro-duodenoscopy (OGD) with dilatation',
    specialities: ['gastric-alimetry', 'general-surgery', 'ent'],
  },
  {
    code: 'G4530',
    treatment: 'Catheterless oesophageal pH monitoring (e.g. Bravo)',
    specialities: ['gastric-alimetry', 'general-surgery', 'ent'],
  },
  {
    code: 'Q3900',
    treatment:
      'Laparoscopy (including e.g. puncture of ovarian cysts, +/- biopsy, minor endometriosis, +/-ureterolysis)',
    specialities: ['gynaecology-and-obstetrics', 'female-fertility'],
  },
  {
    code: 'T5203',
    treatment:
      "Dupuytren's fasciectomy single digit with proximal interphalangeal joint",
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
      'plastic-surgery',
    ],
  },
  {
    code: '20142',
    treatment:
      'Insertion of implantable ECG loop recorder (including reporting)',
    specialities: ['cardiology'],
  },
  {
    code: '20143',
    treatment: 'Removal of implantable ECG loop recorder (including reporting)',
    specialities: ['cardiology'],
  },
  {
    code: 'K6105',
    treatment:
      'Insertion of dual chamber implantable cardioverter defibrillator (ICD)',
    specialities: ['cardiology'],
  },
  {
    code: 'K6111',
    treatment:
      'Insertion of combined biventricular pacemaker and cardioverter defibrillator (CRT-D)',
    specialities: ['cardiology'],
  },
  {
    code: 'A0260',
    treatment: 'Excision of arteriovenous malformation from vessels of brain',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'A3810',
    treatment: 'Excision of lesion of meninges of brain',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'A3830',
    treatment: 'Operation for arachnoidal cyst',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'V2562',
    treatment: 'Decompression for central spinal stenosis (3 or more levels)',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'A5540',
    treatment: 'Spinal angiogram',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'L3330',
    treatment:
      'Endovascular insertion of an intrasaccular wire-mesh blood-flow disruption device for intracranial aneurysms',
    specialities: ['general-surgery'],
  },
  {
    code: 'XR352',
    treatment: 'Embolisation of artery / vein',
    specialities: ['radiology'],
  },
  {
    code: 'C1710',
    treatment: 'Suture of eyelid (laceration) (as sole procedure)',
    specialities: ['ophthalmology'],
  },
  {
    code: 'C7340',
    treatment:
      'YAG (Yttrium Aluminium Garnett) laser photodisruption of posterior capsule of lens (including laser capsulotomy) - unilateral',
    specialities: ['ophthalmology'],
  },
  {
    code: 'W0850',
    treatment: 'Partial excision of bone (including exostoses)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W1040',
    treatment:
      'Osteotomy of short bone of foot (excluding hallux valgus and including internal fixation)',
    specialities: ['trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: '20130',
    treatment: '20130',
    specialities: ['general-surgery', 'cardiology'],
  },
  {
    code: 'X3760',
    treatment:
      'Simple intramuscular injection, without X-ray control (as sole procedure)',
    specialities: ['radiology', 'rheumatology'],
  },
  {
    code: 'X5020',
    treatment: 'External cardioversion',
    specialities: ['radiology', 'cardiology'],
  },
  {
    code: 'C0513',
    treatment: 'Reconstruction of socket with either implant or graft',
    specialities: ['ophthalmology'],
  },
  {
    code: 'H1542',
    treatment: 'Closure of colostomy',
    specialities: ['general-surgery', 'gut-health-test'],
  },
  {
    code: 'S1700',
    treatment: 'Distant flap - delay/division/inset',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'V2020',
    treatment: 'Prosthetic replacement of temporomandibular joint',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'B2800',
    treatment: 'Excision of breast lump/fibroadenoma',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'B3100',
    treatment: 'Reduction mammoplasty - unilateral',
    specialities: ['breast-clinic', 'plastic-surgery'],
  },
  {
    code: 'T9000',
    treatment:
      'Sentinel node mapping and sampling with blue dye or radioactive probe for breast cancer',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'A6530',
    treatment: 'Carpal tunnel release (endoscopic)',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'neurosurgery'],
  },
  {
    code: 'T5222',
    treatment:
      "Dupuytren's dermofasciectomy and graft, or for recurrent disease - single digit",
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'W2502',
    treatment:
      'Closed reduction of fracture of long bone (including cast or percutaneous K-wires)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W3203',
    treatment:
      'Early open reduction and internal fixation of scaphoid fracture, i.e. within 6 weeks of fracture',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: 'W3530',
    treatment: 'Removal of percutaneous wire',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W5930',
    treatment:
      'Fusion of digit joint(s) of hand +/- graft & +/- internal fixation',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'U0080',
    treatment:
      'Harvesting for autologous peripheral blood stem cell transplant',
    specialities: ['general-surgery', 'haematology'],
  },
  {
    code: 'U0100',
    treatment: 'Autologous peripheral blood stem cell transplant',
    specialities: ['general-surgery', 'haematology'],
  },
  {
    code: 'W4240',
    treatment:
      '2 stage revision of total knee replacement for infection - first stage',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W7470',
    treatment:
      'Revision of anterior cruciate ligament reconstruction including autograft/allograft',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'P1920',
    treatment: 'Excision of septum of vagina',
    specialities: ['gynaecology-and-obstetrics'],
  },
  {
    code: 'Q1010',
    treatment:
      'Dilation of cervix uteri and curettage of retained products of conception following miscarriage',
    specialities: ['gynaecology-and-obstetrics'],
  },
  {
    code: 'Q1802',
    treatment:
      'Hysteroscopy with resection of fibroids (excluding morcellation) +/- insertion of Mirena coil',
    specialities: ['gynaecology-and-obstetrics'],
  },
  {
    code: 'Q2232',
    treatment:
      'Open oophorectomy and salpingectomy, +/- biopsy eg. omentum, peritoneum, lymph node (as sole procedure) ? unilateral',
    specialities: ['gynaecology-and-obstetrics'],
  },
  {
    code: 'M2930',
    treatment: 'Removal of prosthesis from ureter (including cystoscopy)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'M3202',
    treatment: 'Operations on ureteric orifice (including endoscopic)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'M4780',
    treatment:
      'Invasive urodynamic assessment including cystoscopy and pressure/flow measurements',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'M5180',
    treatment:
      'Revision combined abdominal and vaginal operations to support outlet of female bladder (including sling procedures and cystoscopy)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'M5520',
    treatment:
      'Implantation of artificial urinary sphincter at bladder neck and/or removal (including cystoscopy)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'S2000',
    treatment:
      'Large island skin flap (9cm2 or more) (e.g. radial forearm) (including closure of secondary defect)',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'S4720',
    treatment: 'Drainage of lesion of skin (including abscess)',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'T7483',
    treatment:
      'Ultrasound guided barbotage of calcific deposits of joint (as sole procedure)',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'T7990',
    treatment:
      'Revision of open or arthroscopic rotator cuff repair =/- decompression',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'physiotherapy'],
  },
  {
    code: 'W2300',
    treatment:
      'Secondary open reduction of fracture of short bone (including intra-articular fracture for delayed/non-union and including bone graft)',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'X3510',
    treatment: 'IV sedation administered by operator',
    specialities: ['radiology', 'ent'],
  },
  {
    code: 'T6220',
    treatment: 'Excision of bursa',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'L8513',
    treatment:
      'Endovenous laser treatment (EVLT) of single venous trunk +/- phlebectomies - bilateral',
    specialities: ['general-surgery'],
  },
  {
    code: 'L8621',
    treatment:
      'Ultrasound-guided foam sclerotherapy of varicose veins - bilateral',
    specialities: ['general-surgery'],
  },
  {
    code: 'XR442',
    treatment: 'Embolisation of varicocele of gonadal vein',
    specialities: ['radiology'],
  },
  {
    code: 'A5230',
    treatment: 'Epidurogram +/- epidural injection',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'W3010',
    treatment: 'Application of external fixation to bone',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'S1420',
    treatment: 'Shave biopsy of lesion of skin',
    specialities: ['dermatology', 'plastic-surgery', 'lumps-and-bumps'],
  },
  {
    code: 'M1910',
    treatment:
      'Construction of ileal conduit (including ureteric implantation)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'M2080',
    treatment:
      'Unilateral replantation of ureter into bladder (including cystoscopy)',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'N2200',
    treatment: 'Operation(s) on seminal vesicle',
    specialities: ['urology-and-andrology'],
  },
  {
    code: 'N2842',
    treatment: 'Frenuloplasty of penis',
    specialities: ['urology-and-andrology', 'sexual-health'],
  },
  {
    code: 'X1410',
    treatment: 'Total pelvic exenteration',
    specialities: ['radiology'],
  },
  {
    code: 'A3900',
    treatment: 'Repair of dura',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'A4400',
    treatment: 'Partial excision of spinal cord',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'A6030',
    treatment: 'Transection of peripheral nerve for neuroma',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'neurosurgery'],
  },
  {
    code: 'T6780',
    treatment: 'Primary repair of Achilles tendon',
    specialities: ['trauma-and-orthopaedics-lower-limb', 'physiotherapy'],
  },
  {
    code: 'T6822',
    treatment:
      'Delayed or secondary repair of Achilles tendon with tendon or fascial graft',
    specialities: ['trauma-and-orthopaedics-lower-limb', 'physiotherapy'],
  },
  {
    code: 'T7292',
    treatment: 'Trigger point injection/Enthesis - more than 1 injection',
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'V2402',
    treatment:
      'Posterior decompression (thoracic region) including spinal cord monitoring',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V2543',
    treatment:
      'Revision of posterior excision of disc prolapse with undercutting facetectomy +/- decompression (lumbar region)',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V2680',
    treatment:
      'Revision anterior discectomy, decompression and anterior fusion +/- instrumentation (lumbar region) including spinal cord monitoring',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V2902',
    treatment: 'Revisional anterior discectomy (cervical region)',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V2990',
    treatment: 'Open door laminoplasty of the cervical region (Hirobyashi)',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V3340',
    treatment:
      'Primary anterior discectomy, decompression and anterior fusion +/- instrumentation - lumbar region (1 or 2 levels) including spinal cord monitoring',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V3720',
    treatment:
      'Posterior fusion +/- instrumentation - cervical region (1 or 2 levels) including spinal cord monitoring',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V4080',
    treatment:
      'Anterolateral access with instrumentation +/- decompression +/- discectomy (including graf stabilisation and all fusion approaches) - lumbar region (1 or 2 levels) including spinal cord monitoring',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V4300',
    treatment: 'Anterior vertebrectomy with decompression and implant',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'V4451',
    treatment: 'Balloon kyphoplasty - single level',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'W0321',
    treatment:
      'Osteotomies (eg Scarf and Akin) for hallux valgus correction with or without +/- internal fixation and +/- soft tissue correction - unilateral',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'W0432',
    treatment:
      'Isolated subtalar fusion or midfoot fusion with autogenous graft',
    specialities: ['trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'W0462',
    treatment:
      'Complex procedure to mid foot or hindfoot with autogenous bone graft (osteotomy/fusion +/- tendon transfers/fixation)',
    specialities: ['trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'W5940',
    treatment:
      'Fusion of interphalangeal joint(s) of toe (including internal fixation) - unilateral',
    specialities: ['trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'W7482',
    treatment: 'Scaphoid lunate ligament reconstruction',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: 'W7850',
    treatment: 'Open arthrolysis of elbow',
    specialities: ['trauma-and-orthopaedics-upper-limb'],
  },
  {
    code: 'A0200',
    treatment: 'Excision of lesion of tissue of brain',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'A0280',
    treatment:
      'Awake craniotomy with ablation of lesion of brain +/- cortical mapping/stereotaxy',
    specialities: ['neurosurgery', 'neurology'],
  },
  {
    code: 'V0110',
    treatment: 'Reconstructive cranioplasty',
    specialities: ['neurosurgery', 'trauma-and-orthopaedics-lower-limb'],
  },
  {
    code: 'W3743',
    treatment:
      '2 stage revision of total hip replacement for infection - second stage',
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
    ],
  },
  {
    code: 'B1012',
    treatment: 'Excision of thyroglossal cyst/tract',
    specialities: ['breast-clinic', 'plastic-surgery', 'dental-services'],
  },
  {
    code: 'D0280',
    treatment: 'Removal of multiple bony exostoses external auditory canal',
    specialities: [
      'ent',
      'audiology-hearing-test-battersea-london',
      'dental-services',
    ],
  },
  {
    code: 'D0702',
    treatment:
      'Aural toilet (including microsuction and/or suction of exteriorised mastoid cavity) including bilateral',
    specialities: [
      'ent',
      'audiology-hearing-test-battersea-london',
      'dental-services',
    ],
  },
  {
    code: 'D1420',
    treatment: 'Myringoplasty',
    specialities: [
      'ent',
      'audiology-hearing-test-battersea-london',
      'dental-services',
    ],
  },
  {
    code: 'D1610',
    treatment: 'Ossiculoplasty',
    specialities: [
      'ent',
      'audiology-hearing-test-battersea-london',
      'dental-services',
    ],
  },
  {
    code: 'E0380',
    treatment: 'Nasal septum cauterisation (and bilateral)',
    specialities: ['ent', 'dental-services'],
  },
  {
    code: 'E0520',
    treatment:
      'Ligation of artery of internal nose (including endoscopic, as sole procedure)',
    specialities: ['ent', 'dental-services'],
  },
  {
    code: 'E1240',
    treatment: 'Vidian neurectomy (including endoscopic)',
    specialities: ['ent', 'dental-services'],
  },
  {
    code: 'E1920',
    treatment: 'Partial pharyngectomy',
    specialities: ['ent', 'dental-services'],
  },
  {
    code: 'E2501',
    treatment:
      'Fibre optic examination of the pharynx +/- biopsy/removal of foreign body',
    specialities: ['ent', 'dental-services'],
  },
  {
    code: 'E3520',
    treatment:
      'Microlaryngoscopy/laryngoscopy +/- biopsy, excision of lesion, polyp or cyst',
    specialities: ['ent', 'dental-services'],
  },
  {
    code: 'F4430',
    treatment:
      'Partial excision of parotid gland and preservation of facial nerve',
    specialities: ['maxillofacial', 'dental-services'],
  },
  {
    code: 'T5202',
    treatment: "Dupuytren's fasciectomy palm only",
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'T7482',
    treatment:
      "Platelet Rich Plasma injection for tendinopathy with/without dry needling and ultrasound guidance'",
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    code: 'T7981',
    treatment:
      'Extensive (greater than 2cm tear) repair of large muscle (including arthroscopic) (excluding rotator cuff)',
    specialities: ['trauma-and-orthopaedics-upper-limb', 'physiotherapy'],
  },
] as const;

/** Normalises user input for comparison: uppercase, no spaces/separators. */
function normalizeCode(value: string): string {
  return value
    .replace(/[\s.\-/]/g, '')
    .trim()
    .toUpperCase();
}

/**
 * Returns the CCSD entry exactly matching the given query (case-insensitive,
 * tolerant of spaces/dashes), or null when the query is not a known code.
 */
export function findCcsdEntry(query: string): CcsdEntry | null {
  const normalized = normalizeCode(query);
  if (!normalized) return null;
  return (
    CCSD_CODES.find((entry) => normalizeCode(entry.code) === normalized) ?? null
  );
}

/**
 * Minimum normalized characters before CCSD code matching kicks in. Code
 * matching is deliberately stricter than treatment-text matching so that short
 * fragments typed by users do not fan out to huge consultant lists.
 */
const MIN_CODE_QUERY_LENGTH = 3;

/**
 * Returns true when the query is a potential CCSD/BUPA code prefix that is
 * still too short to search — i.e. it contains a digit but has fewer than
 * MIN_CODE_QUERY_LENGTH normalized characters. The UI uses this to hold off
 * searching entirely until 3 characters are typed.
 */
export function isCcsdCodeSearchPending(query: string): boolean {
  const normalized = normalizeCode(query);
  return (
    normalized.length > 0 &&
    normalized.length < MIN_CODE_QUERY_LENGTH &&
    /\d/.test(normalized)
  );
}

/**
 * Returns CCSD entries whose code starts with / contains the query, or whose
 * treatment name contains the query text. Used by autocomplete.
 *
 * Code matching only begins after 3 characters; treatment-text matching keeps
 * the 2-character floor.
 */
export function searchCcsdEntries(query: string): CcsdEntry[] {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  const normalized = normalizeCode(trimmed);
  const lower = trimmed.toLowerCase();

  return CCSD_CODES.filter((entry) => {
    if (
      normalized.length >= MIN_CODE_QUERY_LENGTH &&
      normalizeCode(entry.code).includes(normalized)
    ) {
      return true;
    }
    return entry.treatment.toLowerCase().includes(lower);
  });
}

/**
 * Returns true when the query looks like a CCSD/BUPA code search — i.e. it is
 * alphanumeric, contains at least one digit, has at least 3 normalized
 * characters, and matches (as a prefix or substring) at least one known code.
 * Treatment-text searches ("tonsillectomy", "knee pain") return false. Used to
 * switch the search results to a consultants-only view.
 */
export function isCcsdCodeQuery(query: string): boolean {
  const normalized = normalizeCode(query);
  if (
    !normalized ||
    normalized.length < MIN_CODE_QUERY_LENGTH ||
    !/\d/.test(normalized)
  )
    return false;
  return CCSD_CODES.some((entry) =>
    normalizeCode(entry.code).includes(normalized),
  );
}
